import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

// Utility function to add timeout to fetch requests
const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    )
  ]);
};

const SubscriptionUsageCounter = ({ className = "" }) => {
  const { user, isAuthenticated } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'land_seeker') {
      fetchUsage();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchUsage = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUsage(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetchWithTimeout(apiUrl('/subscriptions/usage'), {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }, 10000);

      if (!response.ok) {
        if (response.status !== 404) {
          console.warn('Subscription usage request failed with status', response.status);
        }
        setUsage(null);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setUsage(data.data);
      } else {
        console.warn('Failed to load usage data:', data.message);
        setUsage(null);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
      if (error.message === 'Request timeout') {
        toast.error('Request timed out. Please check your connection and try again.');
      }
      setUsage(null);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || user?.role !== 'land_seeker' || loading) {
    return null;
  }

  if (!usage) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${className}`}>
        <div className="text-center text-gray-500">
          <div className="text-2xl mb-2">📊</div>
          <p>No subscription usage data available</p>
        </div>
      </div>
    );
  }

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getUsageTextColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Subscription Usage</h3>
        <div className="text-sm text-gray-500">
          {usage.subscription.plan_name}
        </div>
      </div>

      {/* Properties Usage */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Property detail views</span>
          <span className={`text-sm font-medium ${getUsageTextColor(usage.usage.properties.percentage)}`}>
            {usage.usage.properties.used} / {usage.usage.properties.limit}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${getUsageColor(usage.usage.properties.percentage)}`}
            style={{ width: `${Math.min(usage.usage.properties.percentage, 100)}%` }}
          ></div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {usage.usage.properties.remaining} views remaining
        </div>
      </div>

      {/* Area Preferences Usage */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Area Preferences</span>
          <span className={`text-sm font-medium ${getUsageTextColor(usage.usage.area_preferences.percentage)}`}>
            {usage.usage.area_preferences.used} / {usage.usage.area_preferences.limit}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${getUsageColor(usage.usage.area_preferences.percentage)}`}
            style={{ width: `${Math.min(usage.usage.area_preferences.percentage, 100)}%` }}
          ></div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {usage.usage.area_preferences.remaining} remaining
        </div>
      </div>

      {/* Summary */}
      <div className="border-t pt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Overall Usage</span>
          <span className={`text-sm font-medium ${getUsageTextColor(usage.summary.total_usage_percentage)}`}>
            {usage.summary.total_usage_percentage}%
          </span>
        </div>
        
        {usage.summary.is_near_limit && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            ⚠️ You're approaching your subscription limits
          </div>
        )}
        
        {usage.summary.is_at_limit && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            🚫 You've reached your subscription limits
          </div>
        )}

        {/* Subscription Expiry */}
        {usage.subscription.expires_at && (
          <div className="mt-2 text-xs text-gray-500">
            Expires: {new Date(usage.subscription.expires_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUsageCounter;

